import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationDialogComponent } from '@shared/ui';
import { CanComponentDeactivate } from '@telespot/shared/util';
import { from, Observable, of, Subject } from 'rxjs';
import { concatMap, every, map, switchMap, take, takeUntil } from 'rxjs/operators';

import { ResourceEditorComponent } from './resource-editor.component';
import { ResourceEditorFacade } from './resource-editor.facade';
import { ResourceEditorPageFacade } from './resource-editor-page.facade';
import { resourceEditorProviders } from './resource-editor.facade.providers';

/**
 * Route-aware host for `ResourceEditorComponent`.
 *
 * Spreads `resourceEditorProviders` so this wrapper owns the shared
 * `ResourceEditorState` / tree / ledger / cascade instances, and adds the
 * `ResourceEditorPageFacade` that owns the persist-now orchestration. The
 * component itself is a thin host: it reads the `:resourceId` route param
 * (absent in create mode), hands it to `facade.initialize(...)`, and reacts to
 * the facade's navigation intents. The editor owns its own chrome, so this
 * host is just the shared editor-page column.
 *
 * Also implements `CanComponentDeactivate` for the route's `ExitEditorGuard`:
 * a dirty session warns before browser-back/route navigation discards it.
 * `exitConfirmed` flips BEFORE the saved/cancelled navigation so the guard
 * doesn't re-prompt an exit the editor already confirmed (the state is still
 * dirty at that point).
 *
 * Before running its own gate the page consults open dialogs that expose a
 * `canDeactivate` gate of their own (topmost first) — inert today (this
 * editor's sub-dialogs expose no gates yet) but it activates automatically
 * when they do, instead of letting `closeOnNavigation` silently dispose a
 * dirty dialog stack.
 */
@Component({
  selector: 'ts-resource-editor-page',
  template: `<ts-resource-editor></ts-resource-editor>`,
  // Shared editor-page column: capped at 1000px and centered, so the auto
  // margins shrink fluidly to zero on narrow viewports and the editor's own
  // 24px padding remains as the breathing-room floor. The explicit width
  // matters: the shell's router-outlet host is a column flexbox, and cross-axis
  // auto margins disable flex stretching — without it the page shrinks to the
  // content's intrinsic width instead of filling the 1000px column.
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        max-width: 1000px;
        margin: 0 auto;
      }
    `,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ResourceEditorComponent],
  providers: [...resourceEditorProviders, ResourceEditorPageFacade],
})
export class ResourceEditorPageComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly destroy$ = new Subject<void>();
  private exitConfirmed = false;

  constructor(
    private readonly facade: ResourceEditorPageFacade,
    private readonly editorFacade: ResourceEditorFacade,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.facade.initialize(this.route.snapshot.paramMap.get('resourceId'));

    this.facade.saved$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.exitConfirmed = true;
      this.location.back();
    });

    this.facade.cancelled$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.exitConfirmed = true;
      this.location.back();
    });
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.exitConfirmed) return true;
    const dialogGates = this.collectDialogGates();
    if (dialogGates.length === 0) return this.confirmOwnExit();
    return from(dialogGates).pipe(
      concatMap((gate) => {
        const result = gate.canDeactivate();
        return (typeof result === 'boolean' ? of(result) : from(result)).pipe(take(1));
      }),
      // Short-circuits on the first refusal, so at most one confirm dialog is
      // open at a time and later gates never run.
      every((allowed) => allowed === true),
      switchMap((dialogsAllow) => {
        if (!dialogsAllow) return of(false);
        const own = this.confirmOwnExit();
        return typeof own === 'boolean' ? of(own) : own;
      })
    );
  }

  /** Open dialogs that expose their own exit gate, topmost first. */
  private collectDialogGates(): CanComponentDeactivate[] {
    return this.dialog.openDialogs
      .map((ref) => ref.componentInstance as Partial<CanComponentDeactivate>)
      .filter((instance): instance is CanComponentDeactivate => typeof instance?.canDeactivate === 'function')
      .reverse();
  }

  private confirmOwnExit(): Observable<boolean> | boolean {
    return this.editorFacade.hasChanges$.pipe(
      take(1),
      switchMap((hasChanges) => {
        if (!hasChanges) return of(true);
        return this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'resource_editor.warnings.cancel_unsaved.title',
              text: 'resource_editor.warnings.cancel_unsaved.text',
              acceptButtonText: 'button.discard_changes',
              cancelButtonText: 'pipeline_step_editor.button.keep_editing',
              warningDialog: true,
              defaultAction: 'cancel',
            },
            hasBackdrop: true,
          })
          .afterClosed()
          .pipe(map((result) => result === 'accept'));
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
